import * as React from "react";
import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Modal from "@mui/material/Modal";
import LogoutIcon from "@mui/icons-material/Logout";
import KeyIcon from "@mui/icons-material/Key";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CloseIcon from "@mui/icons-material/Close";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import ChangePasswordModal from "./ChangePasswordModal";
import ProfileDrawer from "./ProfileDrawer";

import { API_get_referral_program } from "../../../../constants";
import Logout from "@mui/icons-material/ExitToApp";
import { connect } from "react-redux";
import * as actions from "../../../../store/actions/auth";
import { getRightworkAPI } from "../../requests/utilities/requests";
import usePasswordResetStore from "../../../../stores/passwordResetStore";
import useSupportFeedbackStore from "../../../../stores/supportFeedbackStore";

const APP_STORE_REVIEW_URL =
  "https://apps.apple.com/us/app/rightwork/id6470950284?action=write-review";
const PLAY_STORE_REVIEW_URL =
  "https://play.google.com/store/apps/details?id=com.wstewart15.rightwork";
const PUBLIC_URL = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
const ROVIS_MASCOT_SRC = `${PUBLIC_URL}/images/mascot-floating.png`;
const REFERRAL_REWARD = "$250 Amazon gift card";
const REFERRAL_MONTHLY_CAP = "$2,500";

const referralProgramSteps = [
  {
    title: "Copy your code",
    description: "Share your referral code so the intro can be attributed to you during onboarding.",
  },
  {
    title: "Share with a team",
    description: "Send it by text, email, or LinkedIn to another business.",
  },
  {
    title: "Earn a thank you",
    description: `When they become a paid Rightwork customer and stay active for 60 days, you can earn a ${REFERRAL_REWARD} per eligible location.`,
  },
];

const referralFaqs = [
  {
    question: "What qualifies?",
    answer:
      "The referred business must enter or mention your code during onboarding, start a paid Rightwork plan, and remain active for 60 days.",
  },
  {
    question: "When are rewards sent?",
    answer:
      "Rewards are sent to the email on your Rightwork account within 90 days after the referral qualifies.",
  },
  {
    question: "How do multi-location teams work?",
    answer:
      "For multi-location teams, each eligible location can qualify separately once that location starts a paid Rightwork plan and remains active for 60 days.",
  },
  {
    question: "Is there a monthly limit?",
    answer: `You can share as much as you want. Rewards are capped at 10 qualified locations, or ${REFERRAL_MONTHLY_CAP}, per month while the program is active.`,
  },
];

function buildReferralDetails(referralProgram) {
  const referralCode = referralProgram?.referralCode || "";

  if (!referralCode) {
    return {
      referralCode,
      shareOptions: [],
    };
  }

  const referralMessage = `Hey - I thought Rightwork might be useful for your team. It helps teams forecast demand, build better schedules, and keep labor aligned with business needs. If you check it out, mention my referral code during onboarding so Rightwork can attribute the intro to me: ${referralCode}`;
  const linkedInPost = `If scheduling, labor targets, and manager follow-through are still spread across spreadsheets and gut feel, take a look at Rightwork. It helps teams forecast demand, build smarter schedules, and spot labor opportunities sooner.

Referral code: ${referralCode}`;

  return {
    referralCode,
    shareOptions: [
      {
        key: "message",
        title: "Intro message",
        description: "Use this for a text, email, or direct message.",
        value: referralMessage,
        buttonLabel: "Copy Message",
      },
      {
        key: "linkedin",
        title: "LinkedIn post",
        description: "Share from your own voice on LinkedIn.",
        value: linkedInPost,
        buttonLabel: "Copy Post",
      },
    ],
  };
}

function CopyButton({ copied, disabled, label, onClick, variant = "secondary" }) {
  let variantClasses =
    "border-slate-300 bg-white text-slate-700 hover:bg-slate-50 hover:text-slate-900";

  if (variant === "primary") {
    variantClasses =
      "border-violet-200 bg-violet-50 text-violet-700 hover:border-violet-300 hover:bg-violet-100 hover:text-violet-800";
  }

  if (disabled) {
    variantClasses =
      variant === "primary"
        ? "cursor-not-allowed border-violet-100 bg-violet-50 text-violet-300"
        : "cursor-not-allowed border-slate-200 bg-white text-slate-400";
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex h-9 shrink-0 items-center justify-center gap-2 rounded-lg border px-3 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 ${variantClasses}`}
    >
      <ContentCopyIcon style={{ fontSize: "16px" }} />
      {copied ? "Copied" : label}
    </button>
  );
}

function ReferralProgramStep({ step }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
      <div className="mb-2 h-1 w-8 rounded-full bg-violet-600" />
      <h3 className="text-xs font-semibold text-slate-900">
        {step.title}
      </h3>
      <p className="mt-0.5 text-xs leading-5 text-slate-500">
        {step.description}
      </p>
    </div>
  );
}

function ProgramDetailItem({ faq }) {
  const isChainDetail = faq.question === "How do multi-location teams work?";

  return (
    <div
      className={`rounded-lg border px-3 py-3 ${
        isChainDetail
          ? "border-violet-200 bg-violet-50/60"
          : "border-slate-200 bg-white"
      }`}
    >
      <div className="flex items-start gap-2.5">
        <CheckCircleOutlineIcon
          style={{ fontSize: "16px" }}
          className={`mt-0.5 shrink-0 ${
            isChainDetail ? "text-violet-700" : "text-slate-400"
          }`}
        />
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-xs font-semibold text-slate-800">
              {faq.question}
            </p>
            {isChainDetail && (
              <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-700 ring-1 ring-violet-200">
                Per location
              </span>
            )}
          </div>
          <p className="mt-1 text-xs leading-5 text-slate-500">
            {faq.answer}
          </p>
        </div>
      </div>
    </div>
  );
}

function ReferralShareOption({ copiedKey, copyText, onOpenLinkedIn, option }) {
  const isLinkedInOption = option.key === "linkedin";
  const description = isLinkedInOption
    ? "Copy the post, then paste it into LinkedIn."
    : option.description;

  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3.5 py-3">
      <div className="flex items-start justify-between gap-3 sm:flex-col sm:items-stretch">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">
            {option.title}
          </h3>
          <p className="mt-0.5 text-xs leading-5 text-slate-500">
            {description}
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 sm:justify-start">
          <CopyButton
            copied={copiedKey === option.key}
            label={option.buttonLabel}
            onClick={() => copyText(option)}
            variant="primary"
          />
        </div>
      </div>
      <div className="mt-3 whitespace-pre-wrap break-words rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5 text-xs leading-5 text-slate-600">
        {option.value}
      </div>
      {isLinkedInOption && (
        <div className="mt-3 flex justify-end sm:justify-start">
          <button
            type="button"
            onClick={onOpenLinkedIn}
            className="inline-flex h-9 shrink-0 items-center justify-center gap-2 rounded-lg bg-violet-700 px-3 text-sm font-medium text-white transition-colors hover:bg-violet-800 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2"
          >
            <LinkedInIcon style={{ fontSize: "17px" }} />
            Open LinkedIn to Post
          </button>
        </div>
      )}
    </div>
  );
}

function ReviewLink({ href, label }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2"
    >
      {label}
      <OpenInNewIcon style={{ fontSize: "15px" }} />
    </a>
  );
}

function ReferralShareModal({ open, onClose }) {
  const [copiedKey, setCopiedKey] = React.useState(null);
  const [referralProgram, setReferralProgram] = React.useState(null);
  const [loadingReferralProgram, setLoadingReferralProgram] = React.useState(false);
  const [referralProgramError, setReferralProgramError] = React.useState(false);

  const loadReferralProgram = React.useCallback(async () => {
    setLoadingReferralProgram(true);
    setReferralProgramError(false);

    try {
      const response = await getRightworkAPI(API_get_referral_program, {});
      const payload = response?.data?.redux?.payload || {};
      if (!payload.referral_code) {
        throw new Error("Referral payload missing code.");
      }
      setReferralProgram({
        referralCode: payload.referral_code,
      });
    } catch (error) {
      setReferralProgramError(true);
    } finally {
      setLoadingReferralProgram(false);
    }
  }, []);

  const referralDetails = React.useMemo(
    () => buildReferralDetails(referralProgram),
    [referralProgram]
  );

  React.useEffect(() => {
    if (!open) {
      setCopiedKey(null);
      setReferralProgramError(false);
    }
  }, [open]);

  React.useEffect(() => {
    if (open && !referralProgram && !loadingReferralProgram && !referralProgramError) {
      loadReferralProgram();
    }
  }, [
    loadReferralProgram,
    loadingReferralProgram,
    open,
    referralProgram,
    referralProgramError,
  ]);

  const copyText = async (option) => {
    if (!option?.value) return;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(option.value);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = option.value;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "absolute";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      setCopiedKey(option.key);
      window.setTimeout(() => {
        setCopiedKey((current) => (current === option.key ? null : current));
      }, 1400);
    } catch (error) {
      setCopiedKey(null);
    }
  };

  const openLinkedInShare = () => {
    window.open(
      "https://www.linkedin.com/feed/",
      "_blank",
      "noopener,noreferrer"
    );
  };
  const referralProgramLoaded =
    !loadingReferralProgram && !referralProgramError && referralDetails.referralCode;

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="referral-share-title"
    >
      <div className="absolute left-1/2 top-1/2 flex max-h-[88vh] w-[calc(100vw-32px)] max-w-2xl -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-2xl outline-none">
        <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4">
          <div className="flex min-w-0 items-center gap-4 pr-5">
            <img
              src={ROVIS_MASCOT_SRC}
              alt="Rovis"
              className="h-12 w-12 shrink-0 object-contain"
            />
            <div className="min-w-0">
              <h2
                id="referral-share-title"
                className="text-lg font-semibold text-slate-900"
              >
                Refer a business
              </h2>
              <p className="mt-1 text-sm leading-5 text-slate-500">
                Share your referral code with a business and get rewarded.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close referral modal"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2"
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>

        <div className="space-y-4 overflow-y-auto bg-white px-5 py-4 sm:px-4">
          {loadingReferralProgram && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <div className="h-4 w-36 animate-pulse rounded bg-slate-200" />
              <div className="mt-3 h-12 animate-pulse rounded-lg bg-slate-100" />
            </div>
          )}

          {!loadingReferralProgram && referralProgramError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-red-900">
                    Could not load your referral code
                  </h3>
                  <p className="mt-1 text-sm leading-5 text-red-700">
                    Refresh the referral program details before sharing so the intro can be traced back to your account.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={loadReferralProgram}
                  className="inline-flex h-9 shrink-0 items-center justify-center rounded-lg bg-red-700 px-3 text-sm font-medium text-white transition-colors hover:bg-red-800 focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-2"
                >
                  Try Again
                </button>
              </div>
            </div>
          )}

          {referralProgramLoaded && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <div className="flex flex-col gap-4 sm:gap-3">
                <div>
                  <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                    <CheckCircleOutlineIcon style={{ fontSize: "18px" }} className="text-violet-700" />
                    Your referral code
                  </h3>
                  <p className="mt-1 text-sm leading-5 text-slate-500">
                    Ask the referred team to mention this code during onboarding so the referral can be traced back to you.
                  </p>
                </div>
                <div className="flex overflow-hidden rounded-lg border border-slate-200 bg-slate-50 sm:flex-col">
                  <div className="min-w-0 flex-1 bg-white px-3 py-2.5">
                    <p className="text-[11px] font-semibold text-slate-500">
                      Referral code
                    </p>
                    <p className="mt-1 break-all font-mono text-lg font-semibold leading-6 text-slate-900">
                      {referralDetails.referralCode}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 bg-white px-3 sm:py-3">
                    <CopyButton
                      copied={copiedKey === "code"}
                      label="Copy Code"
                      onClick={() =>
                        copyText({
                          key: "code",
                          value: referralDetails.referralCode,
                        })
                      }
                      variant="primary"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-3">
            {referralDetails.shareOptions.map((option) => (
              <ReferralShareOption
                key={option.key}
                copiedKey={copiedKey}
                copyText={copyText}
                onOpenLinkedIn={openLinkedInShare}
                option={option}
              />
            ))}

            {!referralProgramLoaded && !loadingReferralProgram && !referralProgramError && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
                Share options will appear once your referral code loads.
              </div>
            )}
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-3.5">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
              <CardGiftcardIcon style={{ fontSize: "17px" }} className="text-violet-700" />
              How it works
            </div>
            <div className="mt-3 grid grid-cols-3 gap-3 md:grid-cols-1">
              {referralProgramSteps.map((step) => (
                <ReferralProgramStep
                  key={step.title}
                  step={step}
                />
              ))}
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-3.5">
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-sm font-semibold text-slate-900">
                Program details
              </h3>
              <span className="text-[11px] font-medium text-slate-500">
                Referral rewards
              </span>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2.5 md:grid-cols-1">
              {referralFaqs.map((faq) => (
                <ProgramDetailItem
                  key={faq.question}
                  faq={faq}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-slate-200 bg-slate-50 px-5 py-3.5 sm:flex-col sm:items-stretch sm:px-4">
          <div className="flex flex-wrap gap-2">
            <ReviewLink href={APP_STORE_REVIEW_URL} label="App Store review" />
            <ReviewLink href={PLAY_STORE_REVIEW_URL} label="Google Play review" />
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 items-center justify-center rounded-lg border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
}

function Account(props) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const { setPasswordResetFlow } = usePasswordResetStore();
  const { openSupportModal } = useSupportFeedbackStore();
  const resourcesUrl = `${PUBLIC_URL}/resources/`;

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const logoutClicked = () => {
    setPasswordResetFlow(0);
    props.logout();
  };

  const [openChangePassword, setOpenChangePassword] = React.useState(false);
  const handleOpenChangePassword = () => setOpenChangePassword(true);
  const handleCloseChangePassword = () => setOpenChangePassword(false);

  const [openProfile, setOpenProfile] = React.useState(false);
  const handleCloseProfile = () => setOpenProfile(false);
  const [openReferralShare, setOpenReferralShare] = React.useState(false);
  const handleOpenReferralShare = () => setOpenReferralShare(true);
  const handleCloseReferralShare = () => setOpenReferralShare(false);
  const handleOpenResources = () => {
    window.location.assign(resourcesUrl);
  };

  // The sidebar variant lives pinned at the bottom-left of the rail, so its menu
  // opens upward and to the right with a downward-pointing arrow. The default
  // (top-bar) variant keeps its original downward drop with an upward arrow.
  const isSidebar = props.variant === "sidebar";

  return (
    <>
      <div>
        {/* Default (top-bar) variant: dark avatar on a white bar. "sidebar"
            variant inverts to a light avatar so it reads on the dark nav rail,
            and optionally shows the user's label when the rail is expanded. */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            textAlign: "center",
            gap: props.showLabel ? "12px" : 0,
            width: props.showLabel ? "100%" : "auto",
            minWidth: 0,
            cursor: "pointer",
          }}
          onClick={handleClick}
        >
          {(() => {
            const avatar = (
              <div
                style={{
                  backgroundColor: isSidebar ? "#e2e8f0" : "#111827",
                }}
                className={`${
                  isSidebar ? "h-8 w-8 text-sm" : "h-10 w-10 text-base"
                } ${
                  isSidebar ? "text-slate-900" : "text-slate-100"
                } flex shrink-0 items-center justify-center rounded-full`}
              >
                <p>{props.username[0].toUpperCase()}</p>
              </div>
            );
            // The sidebar avatar sits next to its own label / is self-evident,
            // so it skips the "Account" tooltip the top-bar variant uses.
            return isSidebar ? (
              avatar
            ) : (
              <Tooltip title="Account">{avatar}</Tooltip>
            );
          })()}
          {props.showLabel && (
            <span className="min-w-0 truncate text-sm font-medium text-slate-100">
              {props.email || props.username}
            </span>
          )}
        </Box>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          onClick={handleClose}
          PaperProps={{
            elevation: 0,
            sx: {
              overflow: "visible",
              filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
              mt: isSidebar ? 0 : 1.5,
              mb: isSidebar ? 1.5 : 0,
              "& .MuiAvatar-root": {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1,
              },
              "&:before": {
                content: '""',
                display: "block",
                position: "absolute",
                width: 10,
                height: 10,
                bgcolor: "background.paper",
                zIndex: 0,
                ...(isSidebar
                  ? {
                      bottom: 0,
                      left: 20,
                      transform: "translateY(50%) rotate(45deg)",
                    }
                  : {
                      top: 0,
                      right: 14,
                      transform: "translateY(-50%) rotate(45deg)",
                    }),
              },
            },
          }}
          transformOrigin={
            isSidebar
              ? { horizontal: "left", vertical: "bottom" }
              : { horizontal: "right", vertical: "top" }
          }
          anchorOrigin={
            isSidebar
              ? { horizontal: "left", vertical: "top" }
              : { horizontal: "right", vertical: "bottom" }
          }
        >
          <MenuItem
            // onClick={handleOpenProfile}
          >
            <Avatar
              style={{ marginRight: "15px", width: "24px", height: "24px" }}
            />{" "}
            {props.username}
          </MenuItem>

          <Divider />

          <MenuItem onClick={handleOpenChangePassword}>
            <KeyIcon fontSize="small" />
            <p className="ml-2">Change Password</p>
          </MenuItem>
          <MenuItem onClick={handleOpenResources}>
            <MenuBookIcon fontSize="small" />
            <p className="ml-2">Product Guide</p>
          </MenuItem>
          <MenuItem onClick={handleOpenReferralShare}>
            <PersonAddAlt1Icon fontSize="small" />
            <p className="ml-2">Refer a business</p>
          </MenuItem>
          <MenuItem onClick={() => openSupportModal("account-menu")}>
            <SupportAgentIcon fontSize="small" />
            <p className="ml-2">Contact Us</p>
          </MenuItem>
          <MenuItem onClick={logoutClicked}>
            <LogoutIcon>
              <Logout fontSize="small" />
            </LogoutIcon>
            <p className="ml-2">Logout</p>
          </MenuItem>
        </Menu>
      </div>
      <ChangePasswordModal
        open={openChangePassword}
        handleClose={handleCloseChangePassword}
      />
      <ProfileDrawer
        open={openProfile}
        onClose={handleCloseProfile}
      />
      <ReferralShareModal
        open={openReferralShare}
        onClose={handleCloseReferralShare}
      />
    </>
  );
}

const mapStateToProps = (state) => {
  return {
    username: state.user,
    email: state.email,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    logout: () => dispatch(actions.logout()),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Account);
